import DOMPurify from "dompurify";

/**
 * Sanitizador central para el contenido rich-text de la plataforma (SEC-6).
 *
 * Toda salida de HTML almacenado pasa por aquí vía RenderVHtml; los editores
 * (NewRichTextEditor y el web component klv3) lo usan al cargar contenido.
 * El allowlist cubre la unión de lo que generan los dos stacks de Quill:
 *  - legacy: quill 2.0.0-dev.3 + quill-table-ui
 *  - klv3:   quill 2.0.3 + quill-better-table + quill-resize-image + katex
 *
 * Si contenido legítimo se rompe, el ajuste va SOLO en este archivo.
 * Tracker: docs/TODOs/260611-sec6_pasos_implementacion.md (Paso 1).
 */

// Dominios permitidos para los iframes de video que inserta Quill (botón "video").
// DOMPurify elimina iframes por defecto; los re-permitimos solo para estos embeds.
const ALLOWED_IFRAME_SRC =
  /^https:\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com)\/embed\/|^https:\/\/player\.vimeo\.com\/video\//i;

// Igual al default de DOMPurify + `data:image/` (Quill embebe imágenes pegadas como base64).
const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|data:image\/|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

const CONFIG = {
  // Perfiles base de DOMPurify: HTML completo (incluye tablas, listas, img,
  // span/class/style, col/colgroup) + MathML (fórmulas KaTeX del editor klv3).
  USE_PROFILES: { html: true, mathMl: true },
  // iframe: solo pasa el hook de video de abajo.
  // semantics/annotation: los emite KaTeX (NUNCA agregar annotation-xml: vector mXSS conocido).
  ADD_TAGS: ["iframe", "semantics", "annotation"],
  ADD_ATTR: [
    // <a target="_blank"> (DOMPurify lo quita por defecto; el hook fuerza rel=noopener)
    "target",
    // atributos del iframe de video de Quill
    "frameborder",
    "allowfullscreen",
    "allow",
  ],
  // los data-* (data-row de tablas, data-list de listas Quill 2, data-value de
  // fórmulas, data-* de better-table) pasan por ALLOW_DATA_ATTR (default true).
  ALLOWED_URI_REGEXP,
  // Controles de formulario: Quill NUNCA los genera. Aunque sus handlers ya se
  // quitan, los eliminamos del todo (defensa en profundidad: evita inputs/botones
  // inertes y vectores de UI redress dentro del contenido almacenado).
  FORBID_TAGS: ["form", "input", "button", "select", "textarea", "option"],
};

// Hook: iframes solo si su src es un embed de video de dominio conocido.
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName === "iframe") {
    const src =
      (typeof node.getAttribute === "function" && node.getAttribute("src")) ||
      "";
    if (!ALLOWED_IFRAME_SRC.test(src)) {
      if (node.parentNode) node.parentNode.removeChild(node);
    }
  }
});

// Hook: links que abren en otra pestaña no pueden manipular el window original (tabnabbing).
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export const sanitizeRichText = (html) => {
  if (!html || typeof html !== "string") return "";
  return DOMPurify.sanitize(html, CONFIG);
};

export default sanitizeRichText;
